/**
 * Environment Fingerprinting Strategy
 * Captures browser environment on initialization and tick updates
 * Includes critical automation/bot detection signals
 */
import { BaseStrategy } from '../strategy.js';
import { inverseSigmoid, gaussian } from '../math-utils.js';
/**
 * Automation framework globals that indicate bot activity
 * These are injected by various testing/automation frameworks
 */
const AUTOMATION_GLOBALS = [
    '__nightmare',
    '_phantom',
    'callPhantom',
    '__selenium_evaluate',
    '__webdriver_evaluate',
    '__driver_evaluate',
    '__webdriver_script_function',
    '__lastWatirAlert',
    '__lastWatirConfirm',
    '__lastWatirPrompt',
    '_Selenium_IDE_Recorder',
    'domAutomation',
    'domAutomationController',
    'webdriver',
    '_webdriver_script_fn',
    '__webdriver_script_func',
    '__fxdriver_evaluate',
    '__fxdriver_unwrapped',
    '__selenium_unwrapped',
];
/**
 * Get the WebGL renderer string without triggering deprecation warnings.
 * Modern browsers (Firefox 128+, Chrome 113+) return the unmasked renderer
 * directly from gl.RENDERER. Falls back to the deprecated
 * WEBGL_debug_renderer_info extension for older browsers.
 */
function getWebGLRenderer(gl) {
    // Modern browsers: gl.RENDERER now returns the unmasked string directly
    const renderer = gl.getParameter(gl.RENDERER);
    if (renderer && !/^(WebKit WebGL|Mozilla|Google Inc\.)$/i.test(renderer)) {
        return renderer;
    }
    // Fallback for older browsers that return a generic RENDERER string
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    if (debugInfo) {
        return gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
    }
    return renderer !== null && renderer !== void 0 ? renderer : undefined;
}
/**
 * Create a temporary WebGL context, run a callback, then release it.
 */
function withWebGLContext(fn) {
    try {
        const canvas = document.createElement('canvas');
        const gl = (canvas.getContext('webgl') || canvas.getContext('experimental-webgl'));
        if (!gl)
            return { hasWebGL: false, result: undefined };
        try {
            return { hasWebGL: true, result: fn(gl) };
        }
        finally {
            const loseCtx = gl.getExtension('WEBGL_lose_context');
            loseCtx === null || loseCtx === void 0 ? void 0 : loseCtx.loseContext();
        }
    }
    catch (_a) {
        return { hasWebGL: false, result: undefined };
    }
}
export class EnvironmentStrategy extends BaseStrategy {
    constructor() {
        super(...arguments);
        this.name = 'environment';
        this.defaultWeight = 0.08;
        this.data = null;
    }
    start() {
        this.captureEnvironment();
    }
    stop() {
        // Environment data persists
    }
    reset() {
        this.data = null;
    }
    onTick(_timestamp) {
        // Only capture once - environment data is static and creating
        // WebGL contexts on every tick leaks GPU resources
        if (!this.data) {
            this.captureEnvironment();
        }
    }
    score() {
        if (!this.data)
            return undefined;
        const env = this.data;
        const auto = env.automation;
        // ============================================
        // IMMEDIATE DISQUALIFICATION - HIGH CONFIDENCE BOT SIGNALS
        // ============================================
        // P0: navigator.webdriver === true (HIGHEST PRIORITY)
        // This is the most reliable automation indicator
        if (auto.isWebdriver) {
            return 0.05;
        }
        // P0: Automation framework globals detected
        if (auto.hasAutomationGlobals) {
            return 0.1;
        }
        // P0: CDP injection detected (Chrome DevTools Protocol)
        if (auto.hasCDPInjection) {
            return 0.1;
        }
        // P2: Headless browser detection
        // Multiple headless indicators = very likely bot
        const headlessIndicatorCount = [
            auto.hasHeadlessUA,
            auto.hasChromelessRuntime,
            auto.hasSoftwareRenderer,
        ].filter(Boolean).length;
        if (headlessIndicatorCount >= 2) {
            return 0.15;
        }
        // Single strong headless indicator
        if (auto.hasHeadlessUA) {
            return 0.2;
        }
        // ============================================
        // WEIGHTED SCORING FOR OTHER SIGNALS
        // ============================================
        let score = 0;
        let factors = 0;
        // Suspicious indicators
        score += env.suspiciousDimensions ? 0.1 : 1.0;
        score += env.suspiciousRatio ? 0.2 : 1.0;
        score += env.featureInconsistency ? 0.3 : 1.0;
        factors += 3;
        // Browser features
        const featureCount = [
            env.hasWebGL,
            env.hasLocalStorage,
            env.hasSessionStorage,
            env.hasIndexedDB,
        ].filter(Boolean).length;
        score += featureCount / 4;
        factors++;
        // Plugins (only suspicious on desktop)
        if (!env.isMobile) {
            score += inverseSigmoid(env.plugins, -2, -0.5);
            score += (env.plugins > 0 ? 1.0 : 0.1);
            factors += 2;
            // No plugins on desktop Chrome = suspicious (but not conclusive)
            if (auto.hasNoPlugins && env.vendor.includes('Google')) {
                score += 0.3;
                factors++;
            }
        }
        // Chromeless runtime (window.chrome but no chrome.runtime)
        if (auto.hasChromelessRuntime) {
            score += 0.3;
            factors++;
        }
        // Software renderer (SwiftShader)
        if (auto.hasSoftwareRenderer) {
            score += 0.4;
            factors++;
        }
        // Device
        score += gaussian(env.devicePixelRatio, 2, 1.5);
        score += (env.colorDepth === 24 || env.colorDepth === 32) ? 1.0 : 0.4;
        factors += 2;
        return factors > 0 ? score / factors : undefined;
    }
    isMobileDevice() {
        // Multiple checks for mobile detection
        const hasTouchScreen = navigator.maxTouchPoints > 0 || 'ontouchstart' in window;
        const mobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        const smallScreen = window.innerWidth < 768 && window.innerHeight < 1024;
        return (hasTouchScreen && smallScreen) || mobileUA;
    }
    /**
     * Detect automation indicators
     */
    detectAutomation() {
        const isMobile = this.isMobileDevice();
        // 1. navigator.webdriver - HIGHEST PRIORITY
        const isWebdriver = navigator.webdriver === true;
        // 2. Headless UA detection
        const ua = navigator.userAgent;
        const hasHeadlessUA = /HeadlessChrome|Headless/i.test(ua);
        // 3. Chrome without runtime (headless Chrome indicator)
        const win = window;
        const hasChromelessRuntime = !!(win.chrome &&
            typeof win.chrome === 'object' &&
            !win.chrome.runtime);
        // 4. Automation framework globals
        const detectedGlobals = [];
        for (const global of AUTOMATION_GLOBALS) {
            if (global in win) {
                detectedGlobals.push(global);
            }
        }
        const hasAutomationGlobals = detectedGlobals.length > 0;
        // 5. CDP (Chrome DevTools Protocol) injection detection
        // CDP injects properties matching pattern: cdc_ or __cdc_
        const detectedCDPKeys = [];
        try {
            for (const key of Object.keys(win)) {
                if (/^cdc_|^__cdc_/.test(key)) {
                    detectedCDPKeys.push(key);
                }
            }
        }
        catch (_a) {
            // Some environments throw on Object.keys(window)
        }
        const hasCDPInjection = detectedCDPKeys.length > 0;
        // 6. No plugins on desktop (suspicious)
        const hasNoPlugins = !isMobile && navigator.plugins.length === 0;
        // 7. Software renderer detection (SwiftShader, ANGLE without GPU)
        let hasSoftwareRenderer = false;
        const webglResult = withWebGLContext((gl) => getWebGLRenderer(gl));
        if (webglResult.hasWebGL && webglResult.result) {
            const renderer = webglResult.result;
            // SwiftShader = headless Chrome software renderer
            // ANGLE without real GPU = suspicious
            hasSoftwareRenderer = /SwiftShader|Software|LLVMpipe/i.test(renderer);
            // Store for debugging
            this._webglRenderer = renderer;
        }
        return {
            isWebdriver,
            hasHeadlessUA,
            hasChromelessRuntime,
            hasSoftwareRenderer,
            hasNoPlugins,
            hasAutomationGlobals,
            detectedGlobals,
            hasCDPInjection,
            detectedCDPKeys,
        };
    }
    captureEnvironment() {
        try {
            // WebGL detection
            const webgl = withWebGLContext((gl) => getWebGLRenderer(gl));
            const hasWebGL = webgl.hasWebGL;
            const webglRenderer = webgl.result;
            // WebRTC detection
            const hasWebRTC = !!(window.RTCPeerConnection ||
                window.mozRTCPeerConnection ||
                window.webkitRTCPeerConnection);
            // Mobile detection
            const isMobile = this.isMobileDevice();
            const windowWidth = window.innerWidth;
            const windowHeight = window.innerHeight;
            const screenWidth = window.screen.width;
            const screenHeight = window.screen.height;
            // Suspicious dimensions
            const suspiciousDimensions = (windowWidth === 800 && windowHeight === 600) ||
                (windowWidth === 1024 && windowHeight === 768) ||
                (windowWidth === 1280 && windowHeight === 720) ||
                (screenWidth === 800 && screenHeight === 600);
            // Suspicious ratio
            const windowScreenRatio = (windowWidth * windowHeight) / (screenWidth * screenHeight);
            const suspiciousRatio = windowScreenRatio === 1.0 ||
                windowScreenRatio < 0.1 ||
                windowScreenRatio > 1.0;
            // Feature inconsistency
            const hasStorage = typeof localStorage !== 'undefined' && typeof sessionStorage !== 'undefined';
            const featureInconsistency = (navigator.plugins.length === 0 && navigator.mimeTypes.length === 0) ||
                !hasWebGL ||
                !hasStorage;
            // Detect automation indicators
            const automation = this.detectAutomation();
            this.data = {
                screenWidth,
                screenHeight,
                windowWidth,
                windowHeight,
                devicePixelRatio: window.devicePixelRatio,
                colorDepth: window.screen.colorDepth,
                userAgent: navigator.userAgent,
                platform: navigator.platform,
                language: navigator.language,
                languages: navigator.languages ? Array.from(navigator.languages) : [navigator.language],
                hardwareConcurrency: navigator.hardwareConcurrency,
                maxTouchPoints: navigator.maxTouchPoints || 0,
                vendor: navigator.vendor,
                hasWebGL,
                hasWebRTC,
                hasLocalStorage: typeof localStorage !== 'undefined',
                hasSessionStorage: typeof sessionStorage !== 'undefined',
                hasIndexedDB: 'indexedDB' in window,
                plugins: navigator.plugins.length,
                mimeTypes: navigator.mimeTypes.length,
                suspiciousRatio,
                suspiciousDimensions,
                featureInconsistency,
                isMobile,
                timestamp: Date.now(),
                automation,
                webglRenderer,
            };
        }
        catch (_a) {
            // Silent fail - environment capture is best effort
        }
    }
    getDebugInfo() {
        return this.data;
    }
    /**
     * Check if current device is mobile
     * Returns true if mobile, false otherwise
     * Returns null if environment hasn't been captured yet
     */
    isMobile() {
        var _a, _b;
        return (_b = (_a = this.data) === null || _a === void 0 ? void 0 : _a.isMobile) !== null && _b !== void 0 ? _b : null;
    }
    /**
     * Get automation detection results
     * Returns null if environment hasn't been captured yet
     */
    getAutomationIndicators() {
        var _a, _b;
        return (_b = (_a = this.data) === null || _a === void 0 ? void 0 : _a.automation) !== null && _b !== void 0 ? _b : null;
    }
    /**
     * Quick check if any strong automation indicator is present
     * Returns true if likely a bot, false if likely human, null if not checked yet
     */
    isLikelyBot() {
        if (!this.data)
            return null;
        const auto = this.data.automation;
        return (auto.isWebdriver ||
            auto.hasAutomationGlobals ||
            auto.hasCDPInjection ||
            auto.hasHeadlessUA);
    }
}
